import { buildAiErrorDetails, isAbortError, safeFetch } from "./client";
import { parseChatGPTResponse } from "./parseChatGPTResponse";
import { logChatGPTPromptDebug, logChatGPTResponseDebug } from "./promptDebug";
import { prepareDataForChatGPT } from "./prepareDataForChatGPT";
import { registerRunCanceller, throwIfAborted } from "./runLifecycle";
import { waitForRunCompletion } from "./runCompletion";

const buildHeaders = (allowedModels) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${allowedModels?.ApiKey}`,
  "OpenAI-Beta": "assistants=v2",
});

const getAssistantReply = (messages) =>
  messages?.data?.find((message) => message.role === "assistant")?.content?.[0]?.text?.value || "";

const buildFailureResponse = ({
  error,
  errorMessage,
  prompt,
  rawResponse = "",
  runTimeline = null,
  validation = null,
}) => ({
  success: false,
  isFallback: false,
  errorMessage,
  errorCode: error?.code || "",
  errorStatus: error?.status || "",
  error: error || null,
  data: null,
  metadata: {
    promptLength: prompt.length,
    responseLength: rawResponse.length,
    validation,
    parserValidation: null,
    runTimeline,
  },
});

export const analyzeWithChatGPT = async (
  reportData,
  filters,
  reportType,
  originalApiResponse,
  language,
  excelData = null,
  allowedModels = null,
  user = null,
  executionControl = null
) => {
  let prompt = "";
  let validation = null;
  let runTimeline = null;
  const updateProgress = executionControl?.onProgress;

  try {
    const prepared = prepareDataForChatGPT(
      reportData,
      filters,
      reportType,
      originalApiResponse,
      excelData,
      language,
      allowedModels,
      user
    );

    prompt = prepared.prompt;
    validation = prepared.validation;

    logChatGPTPromptDebug({
      allowedModels,
      enhancedData: prepared.enhancedData,
      excelData,
      filters,
      prompt,
      reportType,
      validation,
    });

    if (!validation?.hasData) {
      return buildFailureResponse({
        error: { code: "no_financial_data", status: 422, message: "No financial data available for analysis." },
        errorMessage: "No financial data available for analysis.",
        prompt,
        validation,
      });
    }

    const signal = executionControl?.signal;
    const headers = buildHeaders(allowedModels);

    throwIfAborted(signal);
    updateProgress?.({
      progress: 55,
      hasStartedRun: false,
      stage: "creating-thread",
      title: language === "English" ? "Opening AI session" : "جاري فتح جلسة التحليل",
      description:
        language === "English" ? "A new analysis session is being created." : "يتم إنشاء جلسة جديدة للتحليل.",
    });

    const thread = await safeFetch("https://api.openai.com/v1/threads", { method: "POST", headers, signal }, language);
    updateProgress?.({
      progress: 65,
      hasStartedRun: false,
      stage: "sending-prompt",
      title: language === "English" ? "Sending analysis data" : "جاري إرسال بيانات التحليل",
      description:
        language === "English"
          ? "The structured request is being uploaded to the AI service."
          : "يتم الآن إرسال الطلب المنظم إلى خدمة الذكاء الاصطناعي.",
    });

    await safeFetch(
      `https://api.openai.com/v1/threads/${thread.id}/messages`,
      {
        method: "POST",
        headers,
        signal,
        body: JSON.stringify({ role: "user", content: prompt }),
      },
      language
    );

    const run = await safeFetch(
      `https://api.openai.com/v1/threads/${thread.id}/runs`,
      {
        method: "POST",
        headers,
        signal,
        body: JSON.stringify({
          assistant_id: String(allowedModels?.AssistantId || ""),
          temperature: allowedModels?.Temperature ?? 0,
        }),
      },
      language
    );

    updateProgress?.({
      progress: 75,
      hasStartedRun: true,
      stage: "starting-run",
      title: language === "English" ? "AI execution started" : "بدأ تنفيذ التحليل",
      description:
        language === "English"
          ? "The AI run has started."
          : "بدأ تنفيذ التحليل لدى الذكاء الاصطناعي.",
    });

    registerRunCanceller({
      executionControl,
      headers,
      language,
      runId: run.id,
      threadId: thread.id,
    });

    updateProgress?.({
      progress: 85,
      hasStartedRun: true,
      stage: "waiting-response",
      title: language === "English" ? "Waiting for AI response" : "جاري انتظار الاستجابة",
      description:
        language === "English"
          ? "The AI service is preparing the final JSON report."
          : "تقوم خدمة الذكاء الاصطناعي بإعداد التقرير النهائي بصيغة JSON.",
    });

    runTimeline = await waitForRunCompletion(headers, language, run, thread.id, signal);
    updateProgress?.({
      progress: 92,
      hasStartedRun: true,
      stage: "fetching-response",
      title: language === "English" ? "Fetching final output" : "جاري جلب المخرجات النهائية",
      description:
        language === "English"
          ? "The final response is being retrieved now."
          : "يتم الآن جلب الاستجابة النهائية.",
    });

    const messages = await safeFetch(
      `https://api.openai.com/v1/threads/${thread.id}/messages`,
      { headers, signal },
      language
    );

    const rawResponse = getAssistantReply(messages);
    const parsedResponse = parseChatGPTResponse(rawResponse, language);

    updateProgress?.({
      progress: 97,
      hasStartedRun: true,
      stage: "parsing-response",
      title: language === "English" ? "Validating JSON contract" : "جاري التحقق من عقد JSON",
      description:
        language === "English"
          ? "The response is being validated against the required report schema."
          : "يتم التحقق من الاستجابة مقابل هيكل التقرير المطلوب.",
    });

    logChatGPTResponseDebug({
      parsedResponse,
      rawResponse,
      reportType,
      validation,
      errorMessage: parsedResponse.errorMessage,
    });

    if (!parsedResponse.success) {
      return buildFailureResponse({
        error: {
          code: "invalid_ai_json_contract",
          status: 422,
          message: parsedResponse.errorMessage || "AI response does not match the required JSON contract.",
        },
        errorMessage:
          parsedResponse.errorMessage || "AI response does not match the required JSON contract.",
        prompt,
        rawResponse,
        runTimeline,
        validation: {
          ...validation,
          parserValidation: parsedResponse.validation,
        },
      });
    }

    return {
      success: true,
      isFallback: false,
      errorMessage: "",
      data: {
        success: true,
        report: parsedResponse.report,
      },
      metadata: {
        promptLength: prompt.length,
        responseLength: rawResponse.length,
        validation,
        parserValidation: parsedResponse.validation,
        runTimeline,
      },
    };
  } catch (error) {
    if (isAbortError(error)) throw error;

    const details = buildAiErrorDetails({
      code: error.aiErrorCode,
      language,
      message: error.message,
      status: error.aiErrorStatus,
    });

    logChatGPTResponseDebug({
      parsedResponse: null,
      rawResponse: "",
      reportType,
      validation,
      errorMessage: details.message,
    });

    return buildFailureResponse({
      error: {
        code: details.code,
        status: details.status,
        message: details.message,
        type: error.name,
      },
      errorMessage: details.message,
      prompt,
      runTimeline,
      validation,
    });
  }
};
